/**
 * @fileoverview Opens one of a My Page row's actions from its three-dot menu.
 * @description The action dialogs work from the target in full (its contacts and history), which a
 * table row does not carry, so choosing an action in the row menu first reads the target fresh and
 * then hands back the action with it — the same pair the details modal's action bar produces.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { SALES_CRM_QUERY_KEYS } from '@/features/sales/constants/prospects-query-keys.constants'
import { getMyPageTargetDetails } from '@/features/sales/services/my-page-targets.service'

import type { MyPageActionRequest, MyPageTargetAction } from '@/features/sales/types/my-page.types'

/**
 * @description Builds the function that turns a row-menu choice into an action request.
 * @returns {(action: MyPageTargetAction, targetId: string) => Promise<MyPageActionRequest | null>}
 * Reads the target and resolves with the request, or with null when the target could not be read.
 */
export function useMyPageRowAction() {
  const queryClient = useQueryClient()

  return useCallback(
    async (action: MyPageTargetAction, targetId: string): Promise<MyPageActionRequest | null> => {
      try {
        const target = await queryClient.fetchQuery({
          queryKey: SALES_CRM_QUERY_KEYS.myPageTarget(targetId),
          queryFn: () => getMyPageTargetDetails(targetId),
          staleTime: 0,
        })
        return { action, target }
      } catch {
        return null
      }
    },
    [queryClient],
  )
}
